import { useCallback, useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Receipt, Package, Truck, Money, XCircle, CheckCircle, FileArrowDown } from "@phosphor-icons/react";
import { useShopNotif } from "@/context/ShopNotifContext";

const PAY_BADGE = {
  menunggu_pembayaran: { t: "Menunggu Bayar", c: "bg-blue-100 text-blue-700" },
  menunggu_verifikasi: { t: "Menunggu Verifikasi", c: "bg-amber-100 text-amber-700" },
  paid: { t: "Lunas", c: "bg-emerald-100 text-emerald-700" },
  bukti_ditolak: { t: "Bukti Ditolak", c: "bg-rose-100 text-rose-600" },
  perlu_tinjauan: { t: "Perlu Tinjauan", c: "bg-orange-100 text-orange-700" },
};

const rp = (n) => (n === null || n === undefined ? "—" : "Rp " + (Number(n) || 0).toLocaleString("id-ID"));
const BADGE = {
  menunggu_ongkir: { t: "Menunggu Ongkir", c: "bg-amber-100 text-amber-700" },
  menunggu_pembayaran: { t: "Menunggu Pembayaran", c: "bg-blue-100 text-blue-700" },
  kedaluwarsa: { t: "Kedaluwarsa", c: "bg-slate-200 text-slate-600" },
  dibatalkan: { t: "Dibatalkan", c: "bg-rose-100 text-rose-600" },
  diproses: { t: "Diproses", c: "bg-indigo-100 text-indigo-700" },
  siap_diambil: { t: "Siap Diambil", c: "bg-teal-100 text-teal-700" },
  dikirim: { t: "Dikirim", c: "bg-cyan-100 text-cyan-700" },
  selesai: { t: "Selesai", c: "bg-emerald-100 text-emerald-700" },
};
const FILTERS = [["", "Semua"], ["needs_review", "Perlu Tinjauan"], ["menunggu_ongkir", "Menunggu Ongkir"], ["menunggu_pembayaran", "Menunggu Bayar"], ["diproses", "Diproses"], ["siap_diambil", "Siap Diambil"], ["dikirim", "Dikirim"], ["selesai", "Selesai"], ["kedaluwarsa", "Kedaluwarsa"], ["dibatalkan", "Dibatalkan"]];
const NEXT_LABEL = { siap_diambil: "Tandai Siap Diambil", dikirim: "Tandai Dikirim", selesai: "Tandai Selesai" };
const NEXT_OF = { pickup: { diproses: "siap_diambil", siap_diambil: "selesai" }, delivery: { diproses: "dikirim", dikirim: "selesai" } };
const WA_LABEL = { shop_shipping_awb_issued: "Resi terbit", shop_shipping_in_transit: "Dalam pengiriman", shop_shipping_delivered: "Terkirim", shop_shipping_shipping_problem: "Kendala pengiriman" };

export default function ShopOrdersAdmin() {
  const shopNotif = useShopNotif();
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState("");
  const [sel, setSel] = useState(null);
  const [fee, setFee] = useState("");
  const [reason, setReason] = useState("");
  const [deadline, setDeadline] = useState("");
  const [courier, setCourier] = useState("");
  const [tracking, setTracking] = useState("");
  const [waNotifs, setWaNotifs] = useState([]);

  const loadWa = useCallback(async (orderNo) => {
    try {
      const r = await api.get(`/admin/wa-messages?ref_id=${orderNo}`);
      setWaNotifs((r.data || []).filter((m) => (m.purpose || "").startsWith("shop_shipping_")));
    } catch { setWaNotifs([]); }
  }, []);

  const load = useCallback(() => {
    const q = (filter && filter !== "needs_review") ? `?status=${filter}` : "";
    api.get(`/admin/shop/orders${q}`).then((r) => {
      const list = filter === "needs_review" ? r.data.orders.filter((o) => o.needs_review) : r.data.orders;
      setOrders(list);
    }).catch((e) => toast.error(apiError(e)));
  }, [filter]);
  useEffect(() => {
    document.title = "Pesanan Toko — HalloGBS"; load();
    api.get("/admin/shop/settings").then((r) => setDeadline(String(r.data.payment_deadline_minutes))).catch(() => {});
  }, [load]);

  const saveDeadline = async () => {
    try { const r = await api.put("/admin/shop/settings", { payment_deadline_minutes: Number(deadline) }); setDeadline(String(r.data.payment_deadline_minutes)); toast.success("Batas pembayaran disimpan"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const reconcile = async () => {
    try { const r = await api.post("/admin/shop/reconcile", {}); toast.success(`Rekonsiliasi selesai: ${r.data.expired} pesanan kedaluwarsa`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const open = async (o) => { try { const r = await api.get(`/admin/shop/orders/${o.order_no}`); setSel(r.data); setFee(""); setReason(""); loadWa(o.order_no); shopNotif.refresh(); } catch (e) { toast.error(apiError(e)); } };
  const setShipping = async () => {
    try { const r = await api.post(`/admin/shop/orders/${sel.order_no}/shipping`, { shipping_fee: Number(fee) }); setSel(r.data); toast.success("Ongkir ditetapkan & stok ditahan"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const cancel = async () => {
    if (!reason.trim()) return toast.error("Alasan pembatalan wajib diisi");
    try { const r = await api.post(`/admin/shop/orders/${sel.order_no}/cancel`, { reason: reason.trim() }); setSel(r.data); toast.success("Pesanan dibatalkan"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const verifyPay = async () => {
    try { const r = await api.post(`/admin/shop/orders/${sel.order_no}/payment/verify`); setSel(r.data); toast.success("Pembayaran diverifikasi & stok dikurangi"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const rejectPay = async () => {
    if (!reason.trim()) return toast.error("Alasan penolakan wajib diisi");
    try { const r = await api.post(`/admin/shop/orders/${sel.order_no}/payment/reject`, { reason: reason.trim() }); setSel(r.data); toast.success("Bukti ditolak"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const viewProof = async () => {
    try {
      const r = await api.get(`/admin/shop/orders/${sel.order_no}/payment/proof`, { responseType: "blob" });
      window.open(URL.createObjectURL(r.data), "_blank");
    } catch (e) { toast.error(apiError(e)); }
  };
  const advance = async (target) => {
    const body = { status: target };
    if (target === "dikirim") {
      if (!courier.trim()) return toast.error("Nama kurir wajib diisi");
      body.courier = courier.trim(); body.tracking_no = tracking.trim();
    }
    try { const r = await api.post(`/admin/shop/orders/${sel.order_no}/fulfillment`, body); setSel(r.data); toast.success("Status pesanan diperbarui"); setCourier(""); setTracking(""); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const createShip = async () => {
    try { const r = await api.post(`/admin/shop/orders/${sel.order_no}/shipping/create`); setSel(r.data); loadWa(sel.order_no); toast.success("Pengiriman dibuat"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const refreshShip = async () => {
    try { const r = await api.post(`/admin/shop/orders/${sel.order_no}/shipping/refresh`); setSel(r.data); loadWa(sel.order_no); toast.success("Tracking diperbarui"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const retryWa = async (id) => {
    try { await api.post(`/admin/wa-messages/${id}/retry`); toast.success("Notifikasi WA dikirim ulang"); loadWa(sel.order_no); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-5" data-testid="shop-orders-admin">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="flex items-center gap-2 font-heading text-2xl font-extrabold text-[#4C1D95]"><Receipt size={24} weight="duotone" className="text-[#DB2777]" /> Pesanan Toko</h1>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1.5 rounded-xl border border-pink-100 bg-white px-2.5 py-1.5">
            <span className="text-xs font-bold text-slate-500">Batas bayar (menit)</span>
            <input data-testid="shop-deadline-input" type="number" value={deadline} onChange={(e) => setDeadline(e.target.value)} className="w-16 rounded border border-slate-200 px-1.5 py-1 text-sm" />
            <button data-testid="shop-deadline-save" onClick={saveDeadline} className="rounded-lg bg-[#7C4DE0] px-2.5 py-1 text-xs font-bold text-white">Simpan</button>
          </div>
          <button data-testid="shop-reconcile-btn" onClick={reconcile} className="rounded-xl bg-amber-500 px-3.5 py-2 text-xs font-bold text-white">Rekonsiliasi Kedaluwarsa</button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {FILTERS.map(([v, l]) => {
          const cnt = v === "" ? 0 : (shopNotif.per_status?.[v] || 0);
          return (
            <button key={v} data-testid={`order-filter-${v || "all"}`} onClick={() => setFilter(v)} className={`rounded-full px-3.5 py-1.5 text-xs font-bold ${filter === v ? "bg-[#7C4DE0] text-white" : "bg-white text-slate-600 border border-pink-100"}`}>
              {l}{cnt > 0 && <span data-testid={`order-filter-count-${v}`} className={`ml-1.5 inline-flex min-w-[18px] items-center justify-center rounded-full px-1 text-[10px] font-extrabold ${filter === v ? "bg-white/25 text-white" : "bg-rose-500 text-white"}`}>{cnt}</span>}
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        {orders.map((o) => {
          const b = BADGE[o.status] || { t: o.status, c: "bg-slate-100" };
          return (
            <button key={o.id} data-testid={`admin-order-${o.order_no}`} onClick={() => open(o)} className="rounded-2xl border border-pink-100 bg-white p-4 text-left transition-transform hover:-translate-y-0.5">
              <div className="flex items-center justify-between gap-2"><span className="font-bold text-slate-800">{o.order_no}</span><span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${b.c}`}>{b.t}</span></div>
              <p className="mt-1 flex items-center gap-1.5 text-xs text-slate-500">{o.fulfillment_type === "pickup" ? <Package size={14} /> : <Truck size={14} />} {o.fulfillment_type === "pickup" ? "Ambil di GBS" : "Dikirim"} · {o.items.length} item</p>
              <div className="mt-1 flex items-center justify-between gap-2">
                <p className="text-sm font-extrabold text-[#DB2777]">{o.total === null ? "Menunggu ongkir" : rp(o.total)}</p>
                {PAY_BADGE[o.payment_status] && <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${PAY_BADGE[o.payment_status].c}`}>{PAY_BADGE[o.payment_status].t}</span>}
              </div>
            </button>
          );
        })}
        {orders.length === 0 && <p className="text-sm text-slate-400">Belum ada pesanan.</p>}
      </div>

      {sel && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4" onClick={() => setSel(null)}>
          <div data-testid="admin-order-detail" className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-t-3xl bg-white p-5 sm:rounded-3xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-3 flex items-center justify-between">
              <h2 className="font-heading text-lg font-extrabold text-[#1E1B4B]">{sel.order_no}</h2>
              <button data-testid="admin-detail-close" onClick={() => setSel(null)} className="text-sm font-bold text-slate-500">Tutup</button>
            </div>
            <span className={`inline-block rounded-full px-3 py-1 text-xs font-bold ${(BADGE[sel.status] || {}).c}`}>{(BADGE[sel.status] || {}).t}</span>

            <div className="mt-3 space-y-1.5 rounded-xl bg-[#FAF7FF] p-3 text-sm">
              {sel.items.map((it, i) => (
                <div key={i} className="flex justify-between gap-2"><span className="min-w-0 flex-1 truncate">{it.product_name} · {it.variant_label} ({it.sku}) × {it.qty}</span><span className="shrink-0 font-bold">{it.discount_amount > 0 && <span className="mr-1 text-[11px] font-normal text-slate-400 line-through">{rp(it.price * it.qty)}</span>}{rp(it.line_total)}</span></div>
              ))}
              <div className="flex justify-between border-t border-pink-100 pt-1.5"><span>Subtotal</span><span className="font-bold">{rp(sel.subtotal)}</span></div>
              <div className="flex justify-between"><span>Ongkir</span><span className="font-bold">{sel.shipping_fee === null ? "—" : rp(sel.shipping_fee)}</span></div>
              <div className="flex justify-between text-base"><span className="font-bold">Total</span><span className="font-extrabold text-[#DB2777]">{sel.total === null ? "—" : rp(sel.total)}</span></div>
            </div>

            <div className="mt-3 rounded-xl border border-pink-100 p-3 text-sm">
              <p className="font-bold text-slate-800">{sel.recipient?.name} · {sel.recipient?.wa}</p>
              <p className="mt-0.5 text-slate-500">{sel.fulfillment_type === "pickup" ? "Ambil di GBS" : "Dikirim"}</p>
              {sel.address && <p className="mt-1 text-slate-600">{sel.address.alamat}{sel.address.kode_pos ? `, ${sel.address.kode_pos}` : ""}{sel.address.catatan ? ` (${sel.address.catatan})` : ""}</p>}
            </div>

            {sel.payment_status && (
              <div className="mt-3 rounded-xl border border-pink-100 p-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold uppercase tracking-wide text-slate-500">Pembayaran</span>
                  {PAY_BADGE[sel.payment_status] && <span data-testid="admin-payment-status" className={`rounded-full px-2.5 py-0.5 text-[11px] font-bold ${PAY_BADGE[sel.payment_status].c}`}>{PAY_BADGE[sel.payment_status].t}</span>}
                </div>
                {sel.needs_review && <p className="mt-1.5 rounded-lg bg-orange-50 px-2.5 py-1.5 text-xs text-orange-700">Perlu tinjauan manual: {sel.review_reason === "paid_after_expiry" ? "pembayaran masuk setelah kedaluwarsa/pembatalan" : sel.review_reason}. Putuskan manual (mis. refund) di Midtrans.</p>}
                {sel.payment_status === "menunggu_verifikasi" && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    <button data-testid="admin-view-proof" onClick={viewProof} className="flex items-center gap-1.5 rounded-lg bg-slate-100 px-3 py-1.5 text-xs font-bold text-slate-700"><FileArrowDown size={14} /> Lihat Bukti</button>
                    <button data-testid="admin-verify-pay" onClick={verifyPay} className="flex items-center gap-1.5 rounded-lg bg-emerald-500 px-3 py-1.5 text-xs font-bold text-white"><CheckCircle size={14} weight="fill" /> Verifikasi</button>
                    <button data-testid="admin-reject-pay" onClick={rejectPay} className="flex items-center gap-1.5 rounded-lg bg-rose-500 px-3 py-1.5 text-xs font-bold text-white"><XCircle size={14} weight="fill" /> Tolak (isi alasan di bawah)</button>
                  </div>
                )}
                {sel.reject_reason && <p className="mt-1.5 text-xs text-rose-600">Ditolak: {sel.reject_reason}</p>}
                {sel.paid_at && <p className="mt-1.5 text-xs text-emerald-600">Lunas pada {new Date(sel.paid_at).toLocaleString("id-ID", { dateStyle: "medium", timeStyle: "short" })}</p>}
              </div>
            )}

            {NEXT_OF[sel.fulfillment_type] && NEXT_OF[sel.fulfillment_type][sel.status] && (
              <div className="mt-3 rounded-xl border border-indigo-100 bg-indigo-50/40 p-3" data-testid="admin-fulfillment">
                <p className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">Pemenuhan</p>
                {sel.status === "diproses" && sel.fulfillment_type === "delivery" && (
                  <div className="mb-2 space-y-2">
                    <input data-testid="admin-courier-input" value={courier} onChange={(e) => setCourier(e.target.value)} placeholder="Nama kurir (wajib)" className="w-full rounded-lg border border-indigo-200 px-2.5 py-2 text-sm" />
                    <input data-testid="admin-tracking-input" value={tracking} onChange={(e) => setTracking(e.target.value)} placeholder="Nomor resi (opsional)" className="w-full rounded-lg border border-indigo-200 px-2.5 py-2 text-sm" />
                  </div>
                )}
                <button data-testid="admin-advance-status" onClick={() => advance(NEXT_OF[sel.fulfillment_type][sel.status])} className="flex items-center gap-1.5 rounded-xl bg-indigo-500 px-4 py-2 text-sm font-bold text-white"><Package size={16} weight="fill" /> {NEXT_LABEL[NEXT_OF[sel.fulfillment_type][sel.status]]}</button>
              </div>
            )}
            {sel.shipping && !sel.shipping.manual && (
              <p data-testid="admin-shipping-snapshot" className="mt-2 rounded-lg bg-[#F5F0FF] px-3 py-2 text-xs text-[#6A38C7]">Kurir: <b>{sel.shipping.courier_name} · {sel.shipping.courier_service_name}</b> · Ongkir {rp(sel.shipping.shipping_fee)}{sel.shipping.eta ? ` · Estimasi ${sel.shipping.eta}` : ""}</p>
            )}
            {sel.shipping && sel.shipping.provider === "biteship" && sel.fulfillment_type === "delivery" && (
              <div data-testid="admin-shipping-controls" className="mt-2 rounded-xl border border-[#DDD0FF] bg-white p-3">
                {sel.shipping.biteship_order_id ? (
                  <>
                    <p className="text-xs text-slate-600 break-words">Status kirim: <b data-testid="admin-ship-state">{sel.shipping.state || "menunggu_pickup"}</b>{sel.shipping.waybill_id ? <> · Resi: <b data-testid="admin-ship-awb" className="break-all">{sel.shipping.waybill_id}</b></> : " · Resi: menunggu"}</p>
                    <p className="mt-0.5 text-[11px] text-slate-400">Penjemputan kurir dijadwalkan otomatis bersama order (origin pickup).</p>
                    <button data-testid="admin-ship-refresh" onClick={refreshShip} className="mt-2 rounded-lg bg-[#EEE6FF] px-3 py-1.5 text-xs font-bold text-[#6A38C7]">Refresh Tracking</button>
                  </>
                ) : (
                  <>
                    <p className="text-xs text-amber-700">{sel.shipping.needs_review ? "Pembuatan pengiriman gagal — perlu tinjauan." : "Pengiriman belum dibuat."}</p>
                    <button data-testid="admin-ship-create" onClick={createShip} className="mt-2 rounded-lg bg-[#7C4DE0] px-3 py-1.5 text-xs font-bold text-white">{sel.shipping.needs_review ? "Coba Lagi Buat Pengiriman" : "Buat Pengiriman"}</button>
                  </>
                )}
                {sel.shipping.history && sel.shipping.history.length > 0 && (
                  <div data-testid="admin-ship-timeline" className="mt-2 space-y-1 border-t border-pink-100 pt-2">
                    {sel.shipping.history.slice(-5).reverse().map((h, i) => (
                      <p key={i} className="text-[11px] text-slate-500"><b>{h.state}</b> · {h.status}{h.note ? ` — ${h.note}` : ""}</p>
                    ))}
                  </div>
                )}
                {waNotifs.length > 0 && (
                  <div data-testid="admin-ship-wa" className="mt-2 space-y-1 border-t border-pink-100 pt-2">
                    <p className="text-[11px] font-bold text-slate-500">Notifikasi WhatsApp</p>
                    {waNotifs.map((m) => {
                      const ok = ["accepted_by_gateway", "stubbed", "sent"].includes(m.status);
                      return (
                        <div key={m.id} data-testid={`admin-ship-wa-${m.id}`} className="flex items-center justify-between gap-2">
                          <span className="text-[11px] text-slate-500">{WA_LABEL[m.purpose] || m.purpose} → {m.target_masked}</span>
                          <span className="flex items-center gap-1.5">
                            <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${ok ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-600"}`}>{ok ? "Terkirim" : "Gagal"}</span>
                            {!ok && <button data-testid={`admin-ship-wa-retry-${m.id}`} onClick={() => retryWa(m.id)} className="rounded-lg bg-[#7C4DE0] px-2 py-0.5 text-[10px] font-bold text-white">Coba Ulang</button>}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
            {sel.shipping && sel.shipping.manual && (
              <p data-testid="admin-shipping-manual" className="mt-2 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-700">Ongkir Fallback Manual · {rp(sel.shipping.shipping_fee)}</p>
            )}
            {(sel.courier || sel.tracking_no) && (
              <p data-testid="admin-shipping-info" className="mt-2 text-xs text-slate-500">Kurir: <b>{sel.courier || "—"}</b>{sel.tracking_no ? ` · Resi: ${sel.tracking_no}` : ""}</p>
            )}

            {sel.status === "menunggu_ongkir" && (
              <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 p-3">
                <p className="mb-1 text-[11px] font-bold uppercase tracking-wide text-amber-700">Fallback Manual (Ongkir)</p>
                <p className="mb-2 text-[11px] text-amber-600">Gunakan hanya bila ongkir otomatis Biteship tidak tersedia untuk pesanan ini.</p>
                <div className="flex items-end gap-2">
                  <div className="flex-1"><label className="mb-1 block text-xs font-bold text-slate-500">Ongkir (Rp)</label><input data-testid="admin-fee-input" type="number" value={fee} onChange={(e) => setFee(e.target.value)} className="w-full rounded-lg border border-pink-100 px-2.5 py-2 text-sm" /></div>
                  <button data-testid="admin-set-shipping" onClick={setShipping} className="flex items-center gap-1.5 rounded-xl bg-amber-500 px-4 py-2 text-sm font-bold text-white"><Money size={16} weight="fill" /> Tetapkan Manual</button>
                </div>
              </div>
            )}

            {sel.status !== "dibatalkan" && (
              <div className="mt-3 rounded-xl border border-rose-100 bg-rose-50/50 p-3">
                <label className="mb-1 block text-xs font-bold text-rose-600">Batalkan Pesanan</label>
                <div className="flex items-end gap-2">
                  <input data-testid="admin-cancel-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Alasan pembatalan" className="flex-1 rounded-lg border border-rose-200 px-2.5 py-2 text-sm" />
                  <button data-testid="admin-cancel-order" onClick={cancel} className="flex items-center gap-1.5 rounded-xl bg-rose-500 px-4 py-2 text-sm font-bold text-white"><XCircle size={16} weight="fill" /> Batalkan</button>
                </div>
              </div>
            )}
            {sel.status === "dibatalkan" && sel.cancel_reason && <p className="mt-3 rounded-xl bg-rose-50 p-3 text-sm text-rose-700"><b>Dibatalkan:</b> {sel.cancel_reason}</p>}
          </div>
        </div>
      )}
    </div>
  );
}
